import React, { useEffect, useRef, memo } from 'react';
import injectSheet from 'react-jss';
import Checkmark from 'Assets/ui/checkmark.svg';
import { handleAccessibilityClick } from 'Utils/handleAccessibilityClick';
import styleSheet from './styles';
import { areEqual } from '../../../utils/areEqual';

export interface Props {
  label?: string;
  classes?: any;
  id?: string;
  onClick?: (e: any) => void;
  isChecked?: boolean | undefined;
  value?: boolean | string;
  checkmarkColor?: string;
  children?: any;
  overrideStyles?: {
    margin?: string;
    padding?: string;
    display?: string;
    verticalAlign?: string;
    whiteBoxDisplay?: string;
    checkedColor?: string;
  };
  disallowTabbing?: boolean;
  isFocused?: boolean;
  ariaControls?: string;
}

const CheckBox = (props: Props) => {
  const {
    children,
    classes,
    disallowTabbing,
    id,
    isChecked,
    isFocused,
    label,
    onClick,
    overrideStyles,
    ariaControls,
  } = props;

  const checkBoxStatus: boolean | 'mixed' =
    isChecked === undefined ? 'mixed' : isChecked;

  // clickableAreaRef is used to set focus on the first checkbox, when a user using a screen reader and/or the keyboard
  // opens up the Profile Configuration menu.  There is a useEffect below which handles this
  // Otherwise, as the menu is rendering, focus is sent elsewhere and the user needs multiple tabs to get back to the checkboxes
  const clickableAreaRef = useRef(null);
  useEffect(() => {
    if (props.isFocused && clickableAreaRef && clickableAreaRef.current)
      clickableAreaRef.current.focus();
  }, [isFocused]);

  const handleClick = (event) => {
    if (handleAccessibilityClick(event) && onClick) {
      onClick({
        target: {
          value: checkBoxStatus === 'mixed' ? true : !checkBoxStatus,
          id,
        },
      });
    }
  };

  return (
    <div
      id={id}
      ref={clickableAreaRef}
      role="checkbox"
      aria-checked={checkBoxStatus}
      aria-controls={ariaControls}
      onClick={handleClick}
      className={classes.clickableArea}
      onKeyDown={handleClick}
      tabIndex={(disallowTabbing && -1) || 0}
    >
      <div
        className={`${classes.whiteBox} ${
          checkBoxStatus !== false ? classes.checkedBox : ''
        }`}
      >
        <div className={classes.checkContainer}>
          {checkBoxStatus === true && <Checkmark className={classes.check} />}
          {checkBoxStatus === 'mixed' && (
            <div className={classes.indeterminate} />
          )}
        </div>
      </div>
      {label ? (
        <div
          style={
            checkBoxStatus === true && overrideStyles?.checkedColor
              ? {
                  color: overrideStyles.checkedColor,
                }
              : null
          }
          className={classes.label}
        >
          {label}
        </div>
      ) : (
        children
      )}
    </div>
  );
};

export default injectSheet(styleSheet)(memo(CheckBox, areEqual));
